import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { type Clip, ClipType } from '@air/api/types';
import {
  type Marker,
  PlayerPreview,
  PlayerPreviewContent,
  PlayerPreviewSpritesheet,
  PlayerPreviewTimestamp,
  PlayerPreviewTrigger,
  PlayerSeekbar,
  PlayerSeekbarChapters,
  PlayerSeekbarTimestamps,
  PlayerSeekbarTrack,
  usePlayerSeekChange,
  usePlayerStore,
} from '@air/component-player';
import { useVideoHlsPreview, VideoSeekbarPreviewHls } from '@air/component-video';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { useDiscussionPanelContext } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext';
import { useAssetModalRightPanel } from '~/components/AssetModalV2/AssetModalRightPanelContextProvider';
import { getCommentRangePercentStyle } from '~/components/v2/components/asset-modal/visualizer/video/getCommentRangePercentStyle';
import { useSeekbarChapters } from '~/components/v2/components/asset-modal/visualizer/video/hooks/useSeekbarChapters';
import { SelectedCommentRangePlaybackProgress } from '~/components/v2/components/asset-modal/visualizer/video/SelectedCommentRangePlaybackProgress';
import { VideoCommentRangeBarDim } from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/VideoCommentRangeBarDim';
import { VideoCommentRangeBarHighlight } from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/VideoCommentRangeBarHighlight';
import { VideoCommentRangeBarPlaybackProgress } from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/VideoCommentRangeBarPlaybackProgress';
import { VideoCommentRangeSelector } from '~/components/v2/components/asset-modal/visualizer/video/VideoCommentRangeSelector/VideoCommentRangeSelector';
import { QueryParamNames } from '~/constants/search';
import { type Chapter } from '~/hooks/useAssetVersionChaptersList';
import { activeDiscussionIdParamSelector } from '~/store/router/selectors';
import { setQueryParams } from '~/utils/PathUtils';

export type VideoVisualizerSeekbarProps = {
  chapters?: Chapter[];
  clip: Clip;
  markers?: Marker[];
  Seekbar?: typeof PlayerSeekbar;
};

export const VideoVisualizerSeekbar = memo(
  ({ chapters, clip, markers, Seekbar = PlayerSeekbar }: VideoVisualizerSeekbarProps) => {
    const videoElement = usePlayerStore((state) => state.videoElement);
    const { onPlayerSeekChange } = usePlayerSeekChange();
    const seekbarChapters = useSeekbarChapters(chapters);
    const duration = usePlayerStore((state) => state.duration);
    const [hoveredMarker, setHoveredMarker] = useState<Marker | null>(null);
    const [isCommentRangeDragging, setCommentRangeDragging] = useState(false);
    const { setScrollToDiscussionId } = useDiscussionPanelContext();
    const commentRange = useAnnotationV2Store((state) => state.commentRange);
    const isCommentRangeEnabled = useAnnotationV2Store((state) => state.isCommentRangeEnabled);
    const setAnnotationsEnabled = useAnnotationV2Store((state) => state.setAnnotationsEnabled);
    const { showRightPanelTab } = useAssetModalRightPanel();
    const { onUpdateVideo, videoRef } = useVideoHlsPreview({ clip });
    const activeDiscussionId = useSelector(activeDiscussionIdParamSelector);

    const isAudioClip = clip.type === ClipType.audio;
    const hasSelectedCommentRange =
      isCommentRangeEnabled && !!commentRange && commentRange.startTime !== commentRange.endTime && !isAudioClip;
    const showRangeSelector = isCommentRangeEnabled && !!commentRange && !isAudioClip;

    useEffect(() => {
      if (hasSelectedCommentRange) {
        setHoveredMarker(null);
      }
    }, [hasSelectedCommentRange]);

    useEffect(() => {
      if (!showRangeSelector && isCommentRangeDragging) {
        setCommentRangeDragging(false);
      }
    }, [isCommentRangeDragging, showRangeSelector]);

    const handleMarkerSelect = useCallback(
      async (marker: Marker) => {
        if (activeDiscussionId !== marker.discussion.id) {
          await setQueryParams({ [QueryParamNames.discussionId]: [marker.discussion.id] }, 'replace');
        } else {
          await setQueryParams({ [QueryParamNames.discussionId]: null }, 'replace');
        }

        if (videoElement) {
          onPlayerSeekChange(marker.timestamp);
        }

        setAnnotationsEnabled(true);
        setScrollToDiscussionId(marker.discussion.id);
        showRightPanelTab('comments');
      },
      [
        onPlayerSeekChange,
        showRightPanelTab,
        setAnnotationsEnabled,
        setScrollToDiscussionId,
        videoElement,
        activeDiscussionId,
      ],
    );

    const handlePositionChange = useCallback(
      (position: number) => {
        onUpdateVideo({
          duration: clip.duration ?? 0,
          position,
        }).catch((error) => {
          reportErrorToBugsnag({ context: 'Failed to update video seekbar preview', error });
        });
      },
      [onUpdateVideo, clip.duration],
    );

    const getMarkerHighlight = useCallback(
      (marker: Marker) => {
        return marker.discussion.id === activeDiscussionId;
      },
      [activeDiscussionId],
    );

    const activeMarker = useMemo(
      () => markers?.find((marker) => marker.discussion.id === activeDiscussionId) ?? null,
      [markers, activeDiscussionId],
    );

    const rangeBarMarker = useMemo(() => {
      if (hoveredMarker?.endTimestamp !== undefined) {
        return hoveredMarker;
      }

      if (!hasSelectedCommentRange && activeMarker?.endTimestamp !== undefined) {
        return activeMarker;
      }

      return null;
    }, [hoveredMarker, activeMarker, hasSelectedCommentRange]);

    const seekbar = useMemo(() => {
      const seekbarContent = seekbarChapters?.length ? (
        <PlayerSeekbarChapters chapters={seekbarChapters} />
      ) : (
        <PlayerSeekbarTrack />
      );

      return <Seekbar>{seekbarContent}</Seekbar>;
    }, [Seekbar, seekbarChapters]);

    const timestamps = useMemo(() => {
      if (markers === undefined || markers.length === 0) {
        return null;
      }

      return (
        <PlayerSeekbarTimestamps
          className={hasSelectedCommentRange ? 'opacity-40 transition-opacity' : undefined}
          disabled={hasSelectedCommentRange}
          getMarkerHighlight={getMarkerHighlight}
          markers={markers}
          onMarkerHoverChange={setHoveredMarker}
          onMarkerSelect={handleMarkerSelect}
        />
      );
    }, [getMarkerHighlight, handleMarkerSelect, hasSelectedCommentRange, markers]);

    return (
      // pb-0.5 keeps the selected-comment range pill's 2px bottom overhang inside this wrapper, clear of the modal footer's z-10 layer that starts flush at the row bottom
      <div className="relative w-full shrink-0 px-4 pt-2 pb-0.5">
        {showRangeSelector || timestamps !== null ? (
          <div className="relative h-5">
            {timestamps}
            {showRangeSelector ? <VideoCommentRangeSelector onDragActiveChange={setCommentRangeDragging} /> : null}
          </div>
        ) : null}

        <div className="relative flex h-3 items-end justify-stretch">
          {isAudioClip ? (
            seekbar
          ) : (
            <PlayerPreview onPositionChange={handlePositionChange}>
              <PlayerPreviewTrigger asChild>{seekbar}</PlayerPreviewTrigger>
              <PlayerPreviewContent>
                {clip.assets.spriteSheet ? (
                  <PlayerPreviewSpritesheet clip={clip} />
                ) : (
                  // Backwards compatibility for old clips that haven't been processed to have a spritesheet.
                  <VideoSeekbarPreviewHls clip={clip} ref={videoRef} />
                )}
                <PlayerPreviewTimestamp />
              </PlayerPreviewContent>
            </PlayerPreview>
          )}

          {showRangeSelector ? <VideoCommentRangeBarDim /> : null}
          {showRangeSelector ? <VideoCommentRangeBarHighlight /> : null}
          {showRangeSelector ? <VideoCommentRangeBarPlaybackProgress isRangeDragging={isCommentRangeDragging} /> : null}

          {rangeBarMarker?.endTimestamp !== undefined && duration > 0 ? (
            <div
              className="pointer-events-none absolute -bottom-0.5 h-2 overflow-hidden rounded-full ads:bg-ads-gray-12 bg-gray-12 p-0.5"
              data-testid="SELECTED_COMMENT_RANGE_PILL"
              style={getCommentRangePercentStyle(
                { startTime: rangeBarMarker.timestamp, endTime: rangeBarMarker.endTimestamp },
                duration,
              )}
            >
              <SelectedCommentRangePlaybackProgress
                endTime={rangeBarMarker.endTimestamp}
                startTime={rangeBarMarker.timestamp}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

VideoVisualizerSeekbar.displayName = 'VideoVisualizerSeekbar';
